<template>
  <v-card class="p-0" rounded="xl" style="position: relative">
    <v-overlay :value="!hasPermission" absolute z-index="2" opacity="0.3"></v-overlay>
    <div class="px-2 px-md-7 pt-5 pb-2">
      <div class="d-flex justify-space-between align-center">
        <h6 class="text-subtitle-1 font-weight-bold">
          衛教短影音（直式）
        </h6>

        <v-btn
          v-if="!isEditing"
          color="teal lighten-2"
          class="text-subtitle-2 rounded-lg"
          dark
          style="width: 100px; height: 40px"
          @click="startEdit"
        >
          <v-icon dark size="15" class="mr-2">
            mdi-pencil
          </v-icon>
          編輯
        </v-btn>
        <div v-else class="d-flex align-center justify-end flex-shrink-1" style="gap: 12px;">
          <v-btn
            v-text="'取消'"
            color="grey"
            outlined
            class="text-subtitle-2 rounded-lg"
            :disabled="isSaving"
            @click="cancelEdit"
          />
          <v-btn
            v-text="'儲存'"
            color="teal lighten-2"
            class="text-subtitle-2 rounded-lg white--text"
            :disabled="isSaving"
            :loading="isSaving"
            @click="saveEdit"
          />
        </div>
      </div>

      <!-- 顯示模式 -->
      <div
        v-if="!isEditing"
        class="d-flex my-2 overflow-x-auto"
        style="gap: 12px;"
        :style="{minHeight: $vuetify.breakpoint.smAndDown ? '424px' : '424px'}"
      >
        <v-sheet v-if="!videos.length" height="250" width="100%" class="d-flex align-center justify-center">
          <div class="grey-4--text">
            影音有助於增加診所的品牌力，獲取NP的信任和預約，<br>
            若需專業攝影協助協助，請洽「<a :href="$enum.DENTCO_CS_LINE" target="_blank"
                                         class="link_blue--text">牙醫小幫手客服</a>」
          </div>
        </v-sheet>
        <div
          v-else
          v-for="(item, i) in videos"
          :key="i"
          class="py-3 flex-shrink-0"
          style="width: 225px; max-width: 70%;"
        >
          <v-responsive :aspect-ratio="9/16" class="rounded">
            <YoutubeShort v-if="item.platform === 'youtube-shorts'" :url="item.url"/>
            <InstagramReel v-else-if="item.platform === 'instagram-reels'" :url="item.url"/>
            <FacebookReel v-else-if="item.platform === 'facebook-reels'" :url="item.url"/>
            <div v-else class="w-[225px] h-[400px] bg-gray-200 flex items-center justify-center rounded">
              無法顯示影片
            </div>
          </v-responsive>
        </div>
      </div>

      <!-- 編輯模式 -->

      <v-row v-else no-gutters class="my-2 py-2" style="row-gap: 28px;">
        <v-sheet ref="scrollContainer" height="359" width="100%" class="overflow-y-auto pr-2">
          <v-form ref="form" v-model="valid">
            <draggable
              v-model="editList"
              tag="div"
              handle=".drag-content"
              class="d-flex flex-column"
              style="row-gap: 16px;"
            >
              <v-sheet v-for="(item, index) in editList" :key="index" color="grey-8" class="py-5" rounded>
                <v-row no-gutters class="align-center align-md-start">
                  <v-col cols="2" md="1" class="text-center">
                    <v-btn icon class="drag-content">
                      <v-icon>
                        mdi-cursor-move
                      </v-icon>
                    </v-btn>
                  </v-col>
                  <v-col cols="10" md="11">
                    <v-row
                      no-gutters
                      class="d-flex"
                      style="row-gap: 8px;"
                    >
                      <v-col cols="12" lg="3" class="px-1">
                        <v-select
                          v-model="item.platform"
                          :items="platformOptions"
                          item-text="label"
                          item-value="value"
                          :rules="[v => !!v || '請選擇影音平台']"
                          dense
                          outlined
                          full-width
                          placeholder="選擇影音來源"
                          hide-details="auto"
                          background-color="white"
                          @change="() => validateUrlField(index)"
                        />
                      </v-col>
                      <v-col cols="10" lg="8" class="px-1">
                        <v-text-field
                          v-model="item.url"
                          :ref="`url-${index}`"
                          dense
                          outlined
                          :rules="urlRules(item)"
                          :placeholder="showPlaceholder(item.platform)"
                          style="flex: 1"
                          full-width
                          hide-details="auto"
                          background-color="white"
                        />
                      </v-col>

                      <v-col cols="2" lg="1" class="px-1">
                        <v-btn icon @click="removeItem(index)">
                          <v-icon>mdi-trash-can-outline</v-icon>
                        </v-btn>
                      </v-col>

                    </v-row>
                  </v-col>
                </v-row>
              </v-sheet>
            </draggable>
          </v-form>
        </v-sheet>

        <div class="d-flex align-center" style="width: 100%; gap: 20px;">
          <div class="flex-grow-1">
            <v-btn
              block
              :elevation="0"
              color="grey-8"
              :dark="editList.length < maxShortVideos"
              style="color: var(--v-grey-5-base);"
              :disabled="editList.length >= maxShortVideos"
              @click="addItem"
            >
              <template v-if="editList.length < maxShortVideos">
                <v-icon small class="mr-1">mdi-plus</v-icon>
                點我新增衛教短影音
              </template>
              <template v-else>
                已達系統上限{{ maxShortVideos }}支影音
              </template>
            </v-btn>
          </div>

          <div class="d-flex align-center justify-end flex-shrink-1" style="gap: 12px;">
            <v-btn
              v-text="'取消'"
              color="grey"
              outlined
              class="text-subtitle-2 rounded-lg"
              :disabled="isSaving"
              @click="cancelEdit"
            />
            <v-btn
              v-text="'儲存'"
              color="teal lighten-2"
              class="text-subtitle-2 rounded-lg white--text"
              :disabled="isSaving"
              :loading="isSaving"
              @click="saveEdit"
            />
          </div>
        </div>
      </v-row>
    </div>
  </v-card>
</template>

<script>
import draggable from "vuedraggable";
import YoutubeShort from "@/components/settings/doctor/v2/YoutubeShort.vue";
import InstagramReel from "@/components/settings/doctor/v2/InstagramReel.vue";
import FacebookReel from "@/components/settings/doctor/v2/FacebookReel.vue";
import { getClinicVideosAPI, saveClinicShortVideosAPI } from "@/api";
import { mapGetters } from "vuex";
import { doctorHealthVideos } from "@/api/mockData/tour/onboarding";
import {
  normalizeShortVideoUrl,
  cleanShortVideoUrl,
  validateShortVideoUrl,
  getShortVideoPlaceholder
} from "@/utils/videoUrlHelper";


const URL_PATTERNS = {
  'youtube-shorts': /^https:\/\/(www\.)?youtube\.com\/shorts\/[a-zA-Z0-9_-]{11}\/?$/,
  'instagram-reels': /^https:\/\/(www\.)?instagram\.com\/reel\/[a-zA-Z0-9_-]+\/?$/,
  'facebook-reels': /^https:\/\/(www\.)?facebook\.com\/reel\/[a-zA-Z0-9_-]+\/?$/,
  'tiktok': /^https:\/\/(www\.)?tiktok\.com\/@[\w.-]+\/video\/\d{10,}\/?$/
};

export default {
  name: 'ClinicShortVideoCard',

  props: {
    hasPermission: {
      type: Boolean
    }
  },

  components: {
    draggable,
    FacebookReel,
    InstagramReel,
    YoutubeShort
  },

  data() {
    const defaultVideos = [
      {
        platform: 'youtube-shorts',
        url: 'https://www.youtube.com/shorts/hbz41pd88_g'
      },
      {
        platform: 'youtube-shorts',
        url: 'https://www.youtube.com/shorts/3NOXFIeOw6k'
      },
      {
        platform: 'youtube-shorts',
        url: 'https://www.youtube.com/shorts/JtZhq0orZNU'
      }
    ]
    return {
      defaultVideos,
      videos: [],
      maxShortVideos: 20,
      isEditing: false,
      valid: true,
      editList: [],
      isSaving: false
    };
  },

  computed: {
    ...mapGetters([
      'axiosParamData',
    ]),
    ...mapGetters('tour', {
      currentTourName: 'currentTourName'
    }),
    platformOptions() {
      return [
        {label: 'YouTube', value: 'youtube-shorts'},
        {label: 'Instagram', value: 'instagram-reels'},
        {label: 'Facebook', value: 'facebook-reels'},
        // { label: 'TikTok', value: 'tiktok' } // 若未支援 iframe 可禁用或 fallback
      ];
    }
  },

  mounted() {
    if (this.currentTourName === 'onboarding') {
      this.videos = doctorHealthVideos.short
    } else {
      this.getClinicVideos()
    }
  },

  methods: {
    showPlaceholder(platform) {
      return getShortVideoPlaceholder(platform);
    },
    startEdit() {
      this.editList = JSON.parse(JSON.stringify(this.videos));
      this.isEditing = true;
    },
    cancelEdit() {
      this.isEditing = false;
    },
    saveEdit() {
      console.log(this.$refs.form.validate())
      this.$refs.form.validate();
      if (!this.valid || this.isSaving) return;

      this.isSaving = true;

      // 先標準化網址，再清理
      this.editList = this.editList.map(item => {
        const normalized = normalizeShortVideoUrl(item.url, item.platform);
        return {
          ...item,
          url: cleanShortVideoUrl(normalized)
        };
      });

      saveClinicShortVideosAPI({
        ...this.axiosParamData,
        videos: [...this.editList]
      }).then(() => {
        this.isEditing = false;
        this.getClinicVideos();
      }).finally(() => {
        this.isSaving = false;
      })
    },
    addItem() {
      if (this.editList.length >= this.maxShortVideos) return;

      this.editList.push({platform: '', url: '', type: 'short'});

      this.$nextTick(() => {
        const el = this.$refs.scrollContainer?.$el;
        if (el && el.scrollTo) {
          el.scrollTo({top: el.scrollHeight, behavior: 'smooth'});
        } else if (el) {
          el.scrollTop = el.scrollHeight;
        }
      });
    },
    removeItem(index) {
      this.editList.splice(index, 1);
    },
    validateUrl(item) {
      return validateShortVideoUrl(item.url, item.platform, URL_PATTERNS);
    },
    urlRules(item) {
      return [
        v => {
          if (!v || !v.trim()) return '請輸入影音網址';
          return true;
        },
        v => this.validateUrl(item)
      ];
    },
    validateUrlField(index) {
      this.$nextTick(() => {
        const urlField = this.$refs[`url-${index}`];
        if (urlField?.length && urlField[0] && urlField[0].value) {
          urlField[0].hasInput = true;
          urlField[0].validate();
        }
      });
    },
    async getClinicVideos() {
      const res = await getClinicVideosAPI(this.axiosParamData);
      this.videos = res.data?.short || (this.hasPermission ? [] : this.defaultVideos);
    }
  }
};
</script>

<style scoped>
/* Your styles here */
</style>
